/**
 * กระบี่ไผ่ห่มหมอก (breeze-sword, the starting art): its lock routine and its ท่าใหญ่ (design
 * docs/design/combat/2026-10-11-long-moves.md §1.2). Keys are prefixed per move (reed…, tp…) for the motion / FX / sound
 * lanes. Ideas only; names and choreography are ours.
 */
import { registerLongMove } from './registry';

/** 蘆 กอแขมสามสาย — binds the blade, circles, whirls the foe, three points, the parting cut (ground-bound). */
export const REED = registerLongMove({
  skillId: 'bs-reed',
  kind: 'lock',
  name: 'กอแขมสามสาย',
  opener: { type: 'contact', range: 0, tell: 0 },
  len: 5.4,
  catch: 0.45,
  power: 2.6,
  lunge: 1.2,
  beats: [
    {
      at: 0,
      pose: 'reedBind',
      hits: 0,
      share: 0,
      foe: 'hit',
      fx: 'bindFlash',
      sfx: 'bladeBind',
      cam: 'hold',
    },
    { at: 0.45, pose: 'reedRise', hits: 1, share: 0.1, foe: 'hit', fx: 'cutRise', sfx: 'swordCutLight' },
    {
      at: 1.05,
      pose: 'reedCircleL',
      hits: 1,
      share: 0.1,
      foe: 'spin',
      fx: 'cutFlat',
      sfx: 'swordCutLight',
      cam: 'orbit',
    },
    {
      at: 1.6,
      pose: 'reedCircleR',
      hits: 1,
      share: 0.1,
      foe: 'spin',
      fx: 'cutFlat',
      sfx: 'swordCutLight',
      cam: 'orbit',
    },
    {
      at: 2.15,
      pose: 'reedReeds',
      hits: 3,
      share: 0.24,
      foe: 'hit',
      fx: 'thrustTriple',
      sfx: 'swordThrust3',
    },
    {
      at: 3.0,
      pose: 'reedGather',
      hits: 0,
      share: 0,
      foe: 'kneel',
      fx: 'windGather',
      sfx: 'qiGather',
      cam: 'push',
    },
    {
      at: 3.55,
      pose: 'reedParting',
      hits: 1,
      share: 0.46,
      foe: 'knock',
      dist: 2.0,
      fx: 'cutBig',
      sfx: 'swordFinisher',
      cam: 'shake',
    },
    { at: 4.1, pose: 'reedSheathe', hits: 0, share: 0, foe: 'fall', sfx: 'sheatheClick' },
    { at: 4.85, pose: 'reedStance', hits: 0, share: 0, foe: 'recover' },
  ],
});

/**
 * 颶 พายุป่าไผ่ — the caster becomes the gust: three passes through the foe, a leaf vortex that lifts it, the gather at the
 * apex, the dive. Field opener r 3.0, tell 0.7 s, piece 3.9 s (4.6 s in all) + 0.5 s release. Rhythm: fast, fast, storm, quiet, finisher.
 */
export const TEMPEST = registerLongMove({
  skillId: 'bs-tempest',
  kind: 'ult',
  name: 'พายุป่าไผ่',
  opener: {
    type: 'field',
    range: 0,
    radius: 3.0,
    tell: 0.7,
    tellPose: 'tpPlant',
    tellFx: 'tpLeafRing',
    tellSfx: 'tpRageFlare',
  },
  // 3.9 s piece + the 0.5 s release (tpStance) in which the caster recovers; the foe is down from tpKneel (3.4 s)
  len: 4.4,
  power: 3.8,
  lunge: 0,
  whiff: { pose: 'tpSpent', fx: 'tpLeafScatter', sfx: 'tpSpent', stop: 0.9, rageBack: 10 },
  beats: [
    {
      at: 0,
      pose: 'tpUproot',
      hits: 1,
      share: 0.06,
      foe: 'lift',
      h: 0.6,
      fx: 'tpLeafSpout',
      sfx: 'tpUproot',
      cam: 'push',
      stop: 0.06,
    },
    {
      at: 0.4,
      pose: 'tpPassA',
      hits: 1,
      share: 0.07,
      foe: 'spin',
      move: { kind: 'pass', dist: 3.2 },
      ai: 3,
      fx: 'tpWindLine',
      sfx: 'tpPass1',
      stop: 0.04,
    },
    {
      at: 0.78,
      pose: 'tpPassB',
      hits: 1,
      share: 0.07,
      foe: 'spin',
      move: { kind: 'pass', dist: 3.2 },
      ai: 3,
      fx: 'tpWindLine',
      sfx: 'tpPass2',
      stop: 0.04,
    },
    {
      at: 1.16,
      pose: 'tpPassC',
      hits: 1,
      share: 0.07,
      foe: 'lift',
      h: 1.2,
      move: { kind: 'pass', dist: 3.2 },
      ai: 3,
      fx: 'tpWindTriangle',
      sfx: 'tpPass3',
      cam: 'orbit',
      stop: 0.04,
    },
    {
      at: 1.6,
      pose: 'tpRise',
      hits: 4,
      share: 0.18,
      foe: 'lift',
      h: 1.8,
      move: { kind: 'leap', h: 2.4 },
      fx: 'tpLeafVortex',
      sfx: 'tpVortexLoop',
      cam: 'orbit',
      stop: 0.06,
    },
    {
      at: 2.5,
      pose: 'tpGather',
      hits: 0,
      share: 0,
      foe: 'lift',
      h: 1.8,
      move: { kind: 'hang', h: 2.4 },
      fx: 'tpLeafGather',
      sfx: 'tpWindDraw',
      cam: 'pull',
    },
    {
      at: 3.0,
      pose: 'tpDive',
      hits: 1,
      share: 0.55,
      foe: 'knock',
      dist: 1.6,
      move: { kind: 'dive' },
      ai: 3,
      fx: 'tpGroveBurst',
      sfx: 'tpFinisher',
      cam: 'shake',
      stop: 0.16,
    },
    { at: 3.4, pose: 'tpKneel', hits: 0, share: 0, foe: 'fall', fx: 'tpLeafRain', sfx: 'tpLeafRain' },
    { at: 3.9, pose: 'tpStance', hits: 0, share: 0, foe: 'recover', fx: 'tpRingFade', sfx: 'tpSheathe' },
  ],
});
